import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../utils/api';
import { Button, Badge, Modal, Alert, SearchBar, Spinner, InfoRow, Pagination } from './shared';

const SHARE_BASE_URL = window.location.origin;

const copyShareLink = async (shareId, setSuccessMessage) => {
    const url = `${SHARE_BASE_URL}/share/${shareId}`;
    try {
        await navigator.clipboard.writeText(url);
        setSuccessMessage?.('Link copied to clipboard');
        setTimeout(() => setSuccessMessage?.(null), 3000);
    } catch {
        window.open(url, '_blank');
    }
};

const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('vi-VN') + ' ' + d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
};

const ACTIVE_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' },
];

const DEFAULT_LIMIT = 50;

export default function AdminShares() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [userIdFilter, setUserIdFilter] = useState('');
    const [activeFilter, setActiveFilter] = useState('');
    const [offset, setOffset] = useState(0);
    const [limit, setLimit] = useState(DEFAULT_LIMIT);

    const loadData = useCallback(async (newOffset) => {
        const currentOffset = newOffset !== undefined ? newOffset : offset;
        setLoading(true);
        setError(null);
        try {
            const params = { limit, offset: currentOffset };
            if (userIdFilter.trim()) params.user_id = userIdFilter.trim();
            if (activeFilter) params.is_active = activeFilter === 'true';
            if (searchQuery.trim()) params.search = searchQuery.trim();
            const res = await adminApi.listShares(params);
            setItems(res.items || []);
            setTotal(res.total || 0);
        } catch (err) {
            setError(err.message || 'Failed to load shares');
        } finally {
            setLoading(false);
        }
    }, [userIdFilter, activeFilter, searchQuery, offset, limit]);

    useEffect(() => { loadData(0); setOffset(0); }, [activeFilter, limit]);
    useEffect(() => { loadData(); }, [offset]);

    const handleSearch = () => { setOffset(0); loadData(0); };

    const handlePageChange = (newOffset) => { setOffset(newOffset); };

    const handleLimitChange = (newLimit) => { setLimit(newLimit); setOffset(0); };

    const handleViewItem = async (shareId) => {
        try {
            const item = await adminApi.getShare(shareId);
            setSelectedItem(item);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleToggleActive = async (shareId, currentActive) => {
        try {
            await adminApi.updateShareStatus(shareId, !currentActive);
            setSuccessMessage(`Share ${shareId} ${!currentActive ? 'activated' : 'deactivated'}`);
            setItems(prev => prev.map(i => i.share_id === shareId ? { ...i, is_active: !currentActive } : i));
            if (selectedItem?.share_id === shareId) {
                setSelectedItem(prev => ({ ...prev, is_active: !currentActive }));
            }
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDelete = async (shareId) => {
        if (!confirm(`Delete share "${shareId}"?`)) return;
        try {
            await adminApi.deleteShare(shareId);
            setSuccessMessage(`Deleted "${shareId}"`);
            setSelectedItem(null);
            setItems(prev => prev.filter(i => i.share_id !== shareId));
            setTotal(prev => Math.max(0, prev - 1));
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        }
    };

    const selectCls = "h-9 px-3 pr-8 rounded-lg border border-gray-200 bg-white text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%239ca3af%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px] bg-[right_8px_center] bg-no-repeat";

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">Shared Content</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {total} share{total !== 1 ? 's' : ''} — shared conversations
                    </p>
                </div>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}
            {successMessage && <Alert variant="success" onClose={() => setSuccessMessage(null)} className="mb-4">{successMessage}</Alert>}

            <div className="flex flex-wrap items-center gap-2 mb-4">
                <SearchBar
                    value={searchQuery}
                    onChange={setSearchQuery}
                    onSearch={handleSearch}
                    onRefresh={() => loadData(offset)}
                    placeholder="Search title or share ID..."
                    className="flex-1"
                />
                <input
                    type="text"
                    value={userIdFilter}
                    onChange={(e) => setUserIdFilter(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="User ID"
                    className="h-9 w-28 px-3 rounded-lg border border-gray-200 bg-white text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                />
                <select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)} className={selectCls}>
                    {ACTIVE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
            </div>

            {loading ? (
                <Spinner size="lg" text="Loading..." center />
            ) : items.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">No shares found</div>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/60">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Share ID</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Title</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 w-20">Owner</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 w-20">Status</th>
                                <th className="text-right px-3 py-2.5 text-xs font-medium text-gray-500 w-16">Views</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 w-32">Created</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 w-36">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {items.map((item) => (
                                <tr
                                    key={item.share_id}
                                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                                    onClick={() => handleViewItem(item.share_id)}
                                >
                                    <td className="px-4 py-2.5">
                                        <span className="font-mono text-xs font-medium text-gray-900">{item.share_id}</span>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <span className="text-xs text-gray-700 truncate block max-w-xs">
                                            {item.title || '-'}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <Badge variant="info">{item.owner_user_id}</Badge>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <Badge variant={item.is_active ? 'success' : 'outline'}>
                                            {item.is_active ? 'Active' : 'Inactive'}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-2.5 text-right">
                                        <span className="text-xs text-gray-500 font-mono">{item.view_count}</span>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <span className="text-xs text-gray-500">{formatDate(item.created_at)}</span>
                                    </td>
                                    <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex items-center justify-end gap-1">
                                            <button
                                                className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-blue-600 transition-colors"
                                                title="Copy share link"
                                                onClick={() => copyShareLink(item.share_id, setSuccessMessage)}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                                                    <path d="M12.232 4.232a2.5 2.5 0 013.536 3.536l-1.225 1.224a.75.75 0 001.061 1.06l1.224-1.224a4 4 0 00-5.656-5.656l-3 3a4 4 0 00.225 5.865.75.75 0 00.977-1.138 2.5 2.5 0 01-.142-3.667l3-3z" />
                                                    <path d="M11.603 7.963a.75.75 0 00-.977 1.138 2.5 2.5 0 01.142 3.667l-3 3a2.5 2.5 0 01-3.536-3.536l1.225-1.224a.75.75 0 00-1.061-1.06l-1.224 1.224a4 4 0 105.656 5.656l3-3a4 4 0 00-.225-5.865z" />
                                                </svg>
                                            </button>
                                            <Button
                                                variant="ghost" size="xs"
                                                onClick={() => handleToggleActive(item.share_id, item.is_active)}
                                            >
                                                {item.is_active ? 'Deactivate' : 'Activate'}
                                            </Button>
                                            <Button
                                                variant="ghost" size="xs"
                                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                onClick={() => handleDelete(item.share_id)}
                                            >
                                                Delete
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="px-4 pb-3">
                        <Pagination total={total} limit={limit} offset={offset} onChange={handlePageChange} onLimitChange={handleLimitChange} />
                    </div>
                </div>
            )}

            {selectedItem && (
                <ShareDetailModal
                    item={selectedItem}
                    onClose={() => setSelectedItem(null)}
                    onToggle={() => handleToggleActive(selectedItem.share_id, selectedItem.is_active)}
                    onDelete={() => handleDelete(selectedItem.share_id)}
                    onCopyLink={() => copyShareLink(selectedItem.share_id, setSuccessMessage)}
                />
            )}
        </div>
    );
}

function ShareDetailModal({ item, onClose, onToggle, onDelete, onCopyLink }) {
    const shareUrl = `${SHARE_BASE_URL}/share/${item.share_id}`;

    return (
        <Modal open onClose={onClose} title={item.title || item.share_id} size="lg">
            <Modal.Body>
                <div className="mb-4 flex items-center gap-2 p-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                    <span className="flex-1 text-xs font-mono text-gray-600 truncate">{shareUrl}</span>
                    <Button variant="primary" size="xs" onClick={onCopyLink}>
                        Copy Link
                    </Button>
                    <Button
                        variant="ghost" size="xs"
                        onClick={() => window.open(shareUrl, '_blank')}
                    >
                        Open
                    </Button>
                </div>

                <div className="grid grid-cols-2 gap-x-8 gap-y-0">
                    <InfoRow label="Share ID" value={item.share_id} mono />
                    <InfoRow label="Owner" value={item.owner_user_id} />
                    <InfoRow label="Session" value={item.session_id} mono />
                    <InfoRow label="Status" value={item.is_active ? 'Active' : 'Inactive'} />
                    <InfoRow label="Views" value={item.view_count} />
                    <InfoRow label="Expires" value={formatDate(item.expires_at)} />
                    <InfoRow label="Created" value={formatDate(item.created_at)} />
                    <InfoRow label="Updated" value={formatDate(item.updated_at)} />
                </div>

                {item.description && (
                    <div className="mt-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">Description</p>
                        <p className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
                            {item.description}
                        </p>
                    </div>
                )}

                <div className="mt-4 flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={onToggle}>
                        {item.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                        variant="ghost" size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={onDelete}
                    >
                        Delete Share
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
}
